import { Button } from '@/components/common/Button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import yup from '@/services/yup';
import { loadFilePreview, loadURLPreview } from '@/utils/browsers';
import { checkImageSupportFormat, cn, getFileInfo, getFileNameFromUrl, scrollToTop } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { BookmarkIcon, EllipsisIcon, ImagePlusIcon, Trash2Icon, UploadIcon, XIcon } from 'lucide-react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, UseFieldArrayReturn, useForm, UseFormReturn } from 'react-hook-form';
import CircleAddIcon from '@/assets/images/circleAddIcon.svg';
import { Textarea } from '@/components/common/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { FILE_SIZE_MENU, LIMIT_IMAGES_UPLOAD, MAX_PRICE_SERVICE, PET_WEIGHT_OPTIONS, SHOP_SERVICE, SHOP_SERVICE_NAME, SUPPORTED_FORMATS, SUPPORTED_FORMATS_MENU_UPLOAD } from '@/configs/constants';
import { INFO_TYPE, SHOP_CATEGORY_TYPE, SHOP_SERVICE_NAME_TYPE, SHOP_SERVICE_TYPE } from '@/types/enums';
import FormatNumber from '@/components/react-number-format';
import { useStore } from '@/hooks/useStore';
import { flowResult, toJS } from 'mobx';
import { useParams } from 'react-router-dom';
import { GetShopInfoResponse } from '@/types/http-payload/shop';
import { Checkbox } from '@/components/common/Checkbox';
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/common/Carousel';
import { FileUpload } from '@/types/general';
import { observer } from 'mobx-react-lite';
import { toastify } from '@/utils/toastify';
import EditShopFormFooter from '../EditShopFormFooter';
import SearchShopServiceInput, { getServiceShopNameByServiceName } from '@/components/general/SearchShopServiceInput';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/common/DropdownMenu';
import { ActionDropdownItem } from '@/components/general/ActionDropdownItem';

// validate scheme
const validateSchema = yup.object().shape({
    images: yup
        .array()
        .of(yup.mixed<any | FileUpload>().required('form_error_validate.required'))
        .checkFileImagesOfArrayExt('form_error_validate.shop_image_upload_format')
        .min(1, 'form_error_validate.required')
        .max(LIMIT_IMAGES_UPLOAD, 'form_error_validate.shop_image_upload_number')
        .required('form_error_validate.required'),
    introduction: yup
        .string()
        .required('form_error_validate.required'),
    facebook: yup
        .string(),
    youtube: yup
        .string(),
    instagram: yup
        .string(),
    tiktok: yup
        .string(),
    menuFile: yup
        .mixed<string | File>()
        .checkFileImagesOfExt('form_error_validate.shop_menu_upload_format')
        .nullable(),
    services: yup.array().of(
        yup.object().shape({
            type: yup.number().oneOf(Object.values(SHOP_SERVICE_TYPE)).required(),
            serviceName: yup
                .string()
                .when('type', {
                    is: type => type === SHOP_SERVICE_TYPE.Other || type === SHOP_SERVICE_TYPE.Medical,
                    then: _ => yup.string().max(255).required('form_error_validate.required'),
                    otherwise: _ => yup
                        .string()
                        .required('form_error_validate.required')
                }),
            petWeightFrom: yup
                .string()
                .when('type', {
                    is: type => type === SHOP_SERVICE_TYPE.Other || type === SHOP_SERVICE_TYPE.Medical,
                    then: _ => yup.string().nullable().optional(),
                    otherwise: _ => yup.string().required('form_error_validate.required')
                }),
            petWeightTo: yup
                .string()
                .when('type', {
                    is: type => type === SHOP_SERVICE_TYPE.Other || type === SHOP_SERVICE_TYPE.Medical,
                    then: _ => yup.string().nullable().optional(),
                    otherwise: _ => yup.string().required('form_error_validate.required')
                }),
            description: yup
                .string(),
            isMedicalService: yup
                .boolean()
                .when('type', {
                    is: SHOP_SERVICE_TYPE.Other,
                    then: _ => yup.boolean().required(),
                    otherwise: _ => yup.boolean().nullable().optional()
                }),
            price: yup.lazy((value) => {
                if (value === '') {
                    return yup
                        .string()
                        .when('type', {
                            is: type => type === SHOP_SERVICE_TYPE.Other || type === SHOP_SERVICE_TYPE.Medical,
                            then: _ => yup.string().nullable().optional(),
                            otherwise: _ => yup.string().required('form_error_validate.required')
                        });
                }

                return yup
                    .number()
                    .positive()
                    .max(MAX_PRICE_SERVICE)
                    .when('type', {
                        is: type => type === SHOP_SERVICE_TYPE.Other || type === SHOP_SERVICE_TYPE.Medical,
                        then: _ => yup.string().nullable().optional(),
                        otherwise: _ => yup.string().required('form_error_validate.required')
                    });
            }),
            code: yup.string().optional().nullable()
        })
    ).required('form_error_validate.required')
});

type FormData = yup.InferType<typeof validateSchema>;

export default observer(function ShopInformationTab() {
    // hooks
    const { t } = useTranslation();
    const { code } = useParams();

    const initialValues = {
        images: [],
        introduction: '',
        facebook: '',
        youtube: '',
        instagram: '',
        tiktok: '',
        menuFile: '',
        services: []
    };

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: initialValues
    });

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'services'
    });

    const watchImages = form.watch('images');
    const watchMenuFile = form.watch('menuFile');

    // store
    const {
        modalStore: { showAlertModal, hideModal },
        shopStore: { shopInfo, updateShopInfo, getShopInfo },
        fileStore: { uploadFile }
    } = useStore();

    // state
    const [imagesLoaded, setImagesLoaded] = useState<{
        id: string,
        src: string,
        isFile: boolean
    }[]>([]);
    const [api, setApi] = useState<CarouselApi>();
    const [currentImage, setCurrentImage] = useState(0);
    const labelImagesRef = useRef<HTMLLabelElement>(null);
    const imagesLoadedRef = useRef(imagesLoaded);

    const serviceList = useMemo(() => {
        const mySet = new Set();
        const categoryIds = (shopInfo as GetShopInfoResponse).categoryIds || [];
        if (categoryIds.length && categoryIds.length > 0) {
            if (categoryIds.includes(SHOP_CATEGORY_TYPE.PetShop)) {
                mySet.add(SHOP_SERVICE_TYPE.Dog);
                mySet.add(SHOP_SERVICE_TYPE.Cat);
                mySet.add(SHOP_SERVICE_TYPE.Other);
            }

            if (categoryIds.includes(SHOP_CATEGORY_TYPE.PetHospital)) {
                mySet.add(SHOP_SERVICE_TYPE.Medical);
                mySet.add(SHOP_SERVICE_TYPE.Other);
            }
        }

        return Array.from(mySet as Set<number>)
            .sort((a, b) => a - b)
            .map(String);
    }, [shopInfo]);

    // life cycle
    useEffect(() => {
        if (watchImages?.length > 0) {
            if (watchImages instanceof Array) {
                watchImages.forEach((image, index) => {
                    const imageUploaded = imagesLoaded.find((e) => {
                        if (typeof image === 'string') return e.id === image;
                        return e.id === image.id;
                    });
                    if (imageUploaded) {
                        loadURLPreview(imageUploaded.src, imageUploaded.id);
                    } else {
                        if (typeof image === 'string') {
                            loadURLPreview(image, image);
                            setImagesLoaded((prevState) => {
                                if (!prevState.find(e => e.id === image)) {
                                    const newState = [...prevState, {
                                        id: image,
                                        src: image,
                                        isFile: false
                                    }];
                                    imagesLoadedRef.current = newState;
                                    return newState;
                                }
                                return prevState;
                            });
                        } else {
                            loadFilePreview(image.file, image.id, {
                                nonFreeMemory: true,
                                callbackLoadImage: ({ src }) => {
                                    setImagesLoaded((prevState) => {
                                        if (!prevState.find(e => e.id === image.id)) {
                                            const newState = [...prevState, {
                                                id: image.id,
                                                src,
                                                isFile: true
                                            }];
                                            imagesLoadedRef.current = newState;
                                            return newState;
                                        }
                                        return prevState;
                                    });
                                }
                            });
                        }
                    }
                });
            }
        }
    }, [watchImages]);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!api) {
            return;
        }

        setCurrentImage(api.selectedScrollSnap() + 1);

        api.on('select', () => {
            setCurrentImage(api.selectedScrollSnap() + 1);
        });
    }, [api]);

    useEffect(() => {
        if (form.formState.errors.images && labelImagesRef.current) {
            labelImagesRef.current.focus();
        }
    }, [form.formState.errors]);

    // function
    const fetchData = async () => {
        const res = await flowResult(getShopInfo(code ?? '', { typeInfo: INFO_TYPE.ShopInfo }));
        if (res?.ok && (res.data as GetShopInfoResponse)) {
            const { images, introduction, facebook, youtube, instagram, tiktok, menuFile, services } = res.data as GetShopInfoResponse;
            form.reset({
                images: images ?? '',
                introduction: introduction ?? '',
                facebook: facebook ?? '',
                youtube: youtube ?? '',
                instagram: instagram ?? '',
                tiktok: tiktok ?? '',
                menuFile: menuFile ?? null,
                services: services ?? ''
            });
        }
    };

    const onSubmit = async (data: Omit<FormData, 'images'> & { images: any[] }) => {
        const { images, introduction, facebook, youtube, instagram, tiktok, menuFile, services } = data;

        if (services.length <= 0 &&
            (shopInfo?.categoryIds && shopInfo?.categoryIds.length > 0 &&
                (shopInfo?.categoryIds.includes(SHOP_CATEGORY_TYPE.PetShop) || shopInfo?.categoryIds.includes(SHOP_CATEGORY_TYPE.PetHospital))
            )) {
            showAlertErrorAtLeastService();
            return;
        }

        // Lấy index của ảnh mới để mapping với presignedUrls sau này
        const uploadImageIndexes = images
            .map((img, idx) => (img?.file && img?.id ? idx : -1))
            .filter(idx => idx !== -1);

        const getPresignedUrls = {
            images: uploadImageIndexes.length > 0 ?
                uploadImageIndexes.map(idx => getFileInfo(images[idx].file)[1]) :
                false,
            menuFile: menuFile instanceof File ?
                {
                    extname: getFileInfo(menuFile)[1],
                    filename: getFileInfo(menuFile)[0]
                } :
                false
        };

        let resultGetPresignedUrl;
        if (uploadImageIndexes.length > 0 || menuFile instanceof File) {
            resultGetPresignedUrl = await flowResult(updateShopInfo(code ?? '', {
                getPresignedUrls, introduction, facebook, youtube, instagram, tiktok, services
            }));
        } else {
            // Tạo imagesPayload giữ nguyên thứ tự
            const imagesPayload = images.map((img: any) => ({
                key: img.image ? getFileNameFromUrl(img.image) : '', // ảnh cũ
                alt: img.alt,
                isDefault: img.isDefault,
                name: img.image ?
                    (img.name || getFileNameFromUrl(img.image)) : // ưu tiên trường name, fallback là tên file từ URL
                    (img.file?.name || '')
            }));
            const res = await flowResult(updateShopInfo(code ?? '', {
                images: imagesPayload,
                menuFile: menuFile ? getFileNameFromUrl(menuFile) : '',
                introduction, facebook, youtube, instagram, tiktok, services
            }));

            if (res) {
                fetchData();
                scrollToTop();
                toastify('success', t('messages.update_success'));
            }
        }

        if (resultGetPresignedUrl) {
            const { presignedUrls } = resultGetPresignedUrl.data;
            const filesUpload: any[] = [];

            // Mapping đúng index ảnh mới
            if (presignedUrls?.images && presignedUrls.images.length > 0) {
                presignedUrls.images.forEach((item: any, i: number) => {
                    const idx = uploadImageIndexes[i];
                    filesUpload.push({
                        id: `image_${item.fields.key}`,
                        fileType: getPresignedUrls.images[i],
                        file: images[idx].file,
                        presignedUrl: item,
                        alt: images[idx].alt,
                        isDefault: images[idx].isDefault,
                        index: idx
                    });
                });
            }

            if (presignedUrls?.menuFile) {
                filesUpload.push({
                    id: `image_${presignedUrls?.menuFile.fields.key}`,
                    fileType: getPresignedUrls.menuFile,
                    file: menuFile,
                    presignedUrl: presignedUrls?.menuFile
                });
            }

            if (filesUpload.length > 0) {
                handleUploadFile(filesUpload, () => handleUpdateShopInfoAfterUpload(presignedUrls, images, filesUpload));
            }
        }
    };

    const handleUpdateShopInfoAfterUpload = async (presignedUrls, images, filesUpload) => {
        const { introduction, facebook, youtube, instagram, tiktok, services, menuFile } = form.getValues();

        // Tạo imagesPayload giữ nguyên thứ tự
        const imagesPayload = images.map((img: any, idx: number) => {
            if (img.file) {
                // Ảnh mới: lấy key từ presignedUrls theo đúng index
                const fileUpload = filesUpload.find(fu => fu.index === idx);
                return {
                    key: presignedUrls.images[fileUpload ? filesUpload.indexOf(fileUpload) : idx].fileName,
                    alt: img.alt,
                    isDefault: img.isDefault,
                    name: img.file?.name || ''
                };
            } else {
                // Ảnh cũ
                return {
                    key: getFileNameFromUrl(img.image),
                    alt: img.alt,
                    isDefault: img.isDefault,
                    name: img.name || getFileNameFromUrl(img.image)
                };
            }
        });

        const res = await flowResult(updateShopInfo(code ?? '', {
            images: imagesPayload,
            menuFile: presignedUrls.menuFile?.fileName ?? getFileNameFromUrl(menuFile),
            introduction, facebook, youtube, instagram, tiktok, services
        }));

        if (res) {
            fetchData();
            scrollToTop();
            toastify('success', t('messages.update_success'));
        }
    };

    const handleUploadFile = async (fileUploadList, uploadSuccessCallback) => {
        const uploadPromises = fileUploadList.map((fileUpload: any) => {
            const { bucket, ...payload } = fileUpload.presignedUrl?.fields;
            const data = {
                ...(payload || {}),
                file: fileUpload.file
            };

            return new Promise((resolve, reject) => {
                uploadFile(fileUpload.presignedUrl?.url, data, {
                    onSuccess: () => {
                        resolve(true);
                    },
                    onError: () => {
                        fileUpload.onError();
                        reject(false);
                    }
                });
            });
        });

        try {
            const results = await Promise.all(uploadPromises);

            const allSuccessful = results.every(result => result === true);

            if (allSuccessful) {
                uploadSuccessCallback();
            } else {
                showAlertModal({
                    type: 'error',
                    content: t('messages.api_response_no_message')
                });
            }
        } catch (error) {
            console.error('Error during file upload:', error);
        }
    };

    const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            if (!checkImageSupportFormat(files)) {
                showAlertErrorFormatSupport();
            } else if (watchImages && watchImages.length + files.length > LIMIT_IMAGES_UPLOAD) {
                showAlertErrorReachLimitUpload();
            } else {
                const newImages = Array.from(files).map((file, index) => ({
                    file,
                    alt: '',
                    isDefault: false,
                    id: `${file.name}_${Date.now()}_${index}`
                }));

                let updatedImages: any[] = [];

                if (!watchImages || watchImages.length === 0) {
                    // Nếu mảng rỗng, ảnh đầu tiên là default
                    newImages[0].isDefault = true;
                    updatedImages = [...newImages];
                } else {
                    // Nếu mảng đã có ảnh
                    if (watchImages[0]?.isDefault) {
                        // Ảnh đầu đã là default, thêm ảnh mới vào vị trí thứ 2
                        updatedImages = [
                            watchImages[0],
                            ...newImages,
                            ...watchImages.slice(1)
                        ];
                    } else {
                        // Nếu không, thêm ảnh mới vào đầu mảng
                        const hasDefault = watchImages.some(img => img?.isDefault);
                        if (!hasDefault && newImages.length > 0) {
                            newImages[0].isDefault = true;
                        }
                        updatedImages = [
                            ...newImages,
                            ...watchImages
                        ];
                    }
                }

                form.setValue('images', updatedImages, { shouldValidate: true, shouldDirty: true });
            }
        }
        e.target.value = '';
    };

    const handleMenuFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newData = e.target.files;
        if (newData) {
            if (SUPPORTED_FORMATS_MENU_UPLOAD.includes(newData[0].type) && newData[0].size <= FILE_SIZE_MENU) {
                form.setValue('menuFile', newData[0], { shouldDirty: true });
                form.trigger(['menuFile']);
            } else {
                showAlertErrorFormatSupport();
            }
        }
        e.target.value = '';
    };

    const handleRomoveImg = (index: number) => {
        if (watchImages?.length > 0) {
            const removeImage = watchImages[index] as any;

            if (removeImage?.file && removeImage?.id) {
                const imageUploaded = imagesLoaded.find(e => e.id === removeImage.id);
                if (imageUploaded) {
                    URL.revokeObjectURL(imageUploaded.src);
                }
            }

            // Xóa ảnh khỏi mảng images
            let newImages = [
                ...watchImages.slice(0, index),
                ...watchImages.slice(index + 1)
            ];

            // Nếu ảnh bị xóa là default và vẫn còn ảnh, đặt ảnh đầu tiên là default
            if (removeImage?.isDefault && newImages.length > 0) {
                newImages = newImages.map((img: any, i) => ({
                    ...img,
                    isDefault: i === 0
                }));
            }

            form.setValue(
                'images',
                newImages,
                { shouldValidate: true, shouldDirty: true }
            );
        }
    };

    const handleSetdefaultImg = (index: number) => {
        if (!watchImages || watchImages.length === 0) return;
        const newImages = watchImages.map((img: any, i: number) => ({
            ...img,
            isDefault: i === index
        }));
        // Đưa ảnh được set default lên đầu
        const defaultImg = newImages[index];
        const otherImgs = newImages.filter((_, i) => i !== index);
        form.setValue('images', [defaultImg, ...otherImgs], { shouldValidate: true, shouldDirty: true });
    };

    const freeAllImagesMemory = () => {
        imagesLoadedRef.current.forEach((image) => {
            if (image.isFile) {
                // free memory
                URL.revokeObjectURL(image.src);
            }
        });
    };

    const handleRemoveMenuFile = () => {
        form.setValue('menuFile', null, { shouldDirty: true });
    };

    const showAlertErrorFormatSupport = () => {
        showAlertModal({
            type: 'error',
            content: t('form_error_validate.shop_menu_upload_format'),
            cancelButton: false,
            saveButton: t('button.close'),
            onSave: () => hideModal()
        });
    };

    const showAlertErrorReachLimitUpload = () => {
        showAlertModal({
            type: 'error',
            content: t('form_error_validate.shop_image_upload_limit'),
            cancelButton: false,
            saveButton: t('button.close'),
            onSave: () => hideModal()
        });
    };

    const showAlertErrorAtLeastService = () => {
        showAlertModal({
            type: 'error',
            content: t('form_error_validate.services_at_least'),
            cancelButton: false,
            saveButton: t('button.close'),
            onSave: () => hideModal()
        });
    };

    return (
        <>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='my-4'>
                    <div className='bg-white rounded p-4 mt-4'>
                        <FormField
                            control={form.control}
                            name='images'
                            render={({ field: { onChange, value, ...rest } }) => (
                                <>
                                    <FormItem>
                                        <div className='flex justify-between mb-4'>
                                            <div>
                                                <Label className='label-1' required>{t('words_title.shop_images')}</Label>
                                                <FormDescription className='text-[0.8125rem] leading-[1.125rem] text-text-4 pb-1.5'>{t('sentences.upload_images_shop_desc_1')}</FormDescription>
                                            </div>
                                            <div className=''>
                                                <FormControl>
                                                    <Input
                                                        id='shop-images'
                                                        type='file'
                                                        onChange={(event) => {
                                                            handleImageFileChange(event);
                                                        }}
                                                        className='hidden'
                                                        accept={SUPPORTED_FORMATS.join(', ')}
                                                        multiple
                                                        {...rest}
                                                    />
                                                </FormControl>
                                                <Label
                                                    ref={labelImagesRef}
                                                    htmlFor='shop-images'
                                                    tabIndex={0}
                                                    className='flex items-center justify-center cursor-pointer border border-input border-[#9A6051]  rounded-lg p-2 '
                                                >
                                                    <ImagePlusIcon className='text-active h-6 w-6 mr-2' />
                                                    <span className='font-medium leading-4 text-[#9A6051]'>{t('button.add_shop_image')}</span>
                                                </Label>
                                            </div>
                                        </div>
                                        {form.formState.errors?.images?.root?.type === 'min' ?
                                            (
                                                <p className='text-sm font-medium text-destructive whitespace-pre-line'>
                                                    {t(`${form.formState.errors?.images?.root?.message}`)}
                                                </p>
                                            ) :
                                            (
                                                <FormMessage />
                                            )}
                                    </FormItem>
                                    <div className='w-full'>
                                        <div className='overflow-y-auto max-h-[700px]'>
                                            {watchImages?.length > 0 && watchImages.map((image: any, index) => (
                                                <div
                                                    key={index}
                                                    className={`flex items-center px-4 border-b border-dashed last:border-none h-[70px] ${image?.isDefault ? 'bg-[#FFF3F0]' : ''}`}
                                                >
                                                    <div className='flex w-[280px] h-[66px] '>
                                                        <div className='text-sm text-gray-600 mr-1 mt-1 w-6'>{index + 1}</div>
                                                        <div className='w-[50px] h-[50px] my-auto'>
                                                            <img
                                                                className='w-[50px] h-[50px] object-cover rounded mr-2 my-auto'
                                                                id={typeof image === 'string' ? image : image.id}
                                                                alt=''
                                                                src={image?.image}
                                                            />
                                                        </div>
                                                        <div className='mt-1 flex flex-col justify-between items-start mx-2'>
                                                            <div className='truncate-text max-w-[180px]' title={image?.name || image?.file?.name || image?.key}>{image?.name || image?.file?.name || image?.key}</div>
                                                            {image?.isDefault && (
                                                                <div className='text-[#9A6051] text-[12px] font-semibold inline-block mb-2'>
                                                                    <div className='flex items-center border border-[#9A6051] rounded-3xl p-[1px] px-1'>
                                                                        <div>{t('words_title.default')}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className='flex-1'>
                                                        <FormField
                                                            control={form.control}
                                                            name={`images.${index}.alt`}
                                                            render={({ field }) => (
                                                                <FormItem className='flex flex-col w-full'>
                                                                    <Label className='label-1 text-[14px]'>{t('words_title.alt_text')}</Label>
                                                                    <FormControl className='mt-1'>
                                                                        <Input
                                                                            {...field}
                                                                            className={`h-[28px] ${image?.isDefault ? 'bg-[#FFF3F0]' : ''}`}
                                                                            maxLength={255}
                                                                            value={watchImages[index]?.alt ?? ''}
                                                                        />
                                                                    </FormControl>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>

                                                    <div className='ml-10'>
                                                        <ActionMenuDropdown index={index} isDefault={image?.isDefault} handleRomoveImg={handleRomoveImg} handleSetdefaultImg={handleSetdefaultImg}></ActionMenuDropdown>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <p className='text-left text-sm text-gray-500 mt-2'>
                                            {watchImages.length}
                                            {' '}
                                            {t('words_title.total_image_upload')}
                                        </p>
                                    </div>
                                </>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name='introduction'
                            render={({ field }) => (
                                <FormItem className='flex flex-col mt-4 w-full'>
                                    <Label className='label-1' required>{t('words_title.shop_introduction')}</Label>
                                    <FormControl className='mt-1.5'>
                                        <Textarea maxLength={1000} placeholder={t('placeholder.st_about_shop')} {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className='flex md:flex-row flex-col md:gap-4 gap-0'>
                            <FormField
                                control={form.control}
                                name='facebook'
                                render={({ field }) => (
                                    <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                        <Label className='label-1'>{t('words_title.facebook')}</Label>
                                        <FormControl className='mt-1.5'>
                                            <Input
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='youtube'
                                render={({ field }) => (
                                    <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                        <Label className='label-1'>{t('words_title.youtube')}</Label>
                                        <FormControl className='mt-1.5'>
                                            <Input
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='flex md:flex-row flex-col md:gap-4 gap-0'>
                            <FormField
                                control={form.control}
                                name='instagram'
                                render={({ field }) => (
                                    <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                        <Label className='label-1'>{t('words_title.instagram')}</Label>
                                        <FormControl className='mt-1.5'>
                                            <Input
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='tiktok'
                                render={({ field }) => (
                                    <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                        <Label className='label-1'>{t('words_title.tiktok')}</Label>
                                        <FormControl className='mt-1.5'>
                                            <Input
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className='bg-white mt-4 rounded p-4'>
                        <FormField
                            control={form.control}
                            name='menuFile'
                            render={({ field: { onChange, value, ...rest } }) => (
                                <FormItem>
                                    <Label className='label-1'>{t('words_title.upload_menu')}</Label>
                                    {/* <FormDescription className='text-[0.8125rem] leading-[1.125rem] text-text-4'>{t('sentences.upload_menu_shop_desc_1')}</FormDescription> */}
                                    <FormDescription className='text-[0.8125rem] leading-[1.125rem] text-text-4 pb-1.5'>{t('sentences.upload_menu_shop_desc_1')}</FormDescription>
                                    <FormControl>
                                        <Input
                                            id='menu-upload'
                                            type='file'
                                            className='hidden'
                                            onChange={(event) => {
                                                handleMenuFileChange(event);
                                            }}
                                            accept={SUPPORTED_FORMATS_MENU_UPLOAD.join(', ')}
                                            {...rest}
                                        />
                                    </FormControl>
                                    <Label htmlFor='menu-upload' className='flex items-center justify-center cursor-pointer border border-input rounded-lg py-3'>
                                        <UploadIcon className='text-black h-6 w-6 mr-2' />
                                        <span className='text-placeholder-1 font-medium leading-4'>{t('button.upload_shop_menu')}</span>
                                    </Label>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {
                            watchMenuFile && (
                                <div className='border border-input rounded-lg px-2 py-3 flex items-center justify-between mt-2 bg-background-2'>
                                    <span>{watchMenuFile instanceof File ? watchMenuFile.name : watchMenuFile.substring(watchMenuFile.lastIndexOf('/') + 1)}</span>
                                    <button
                                        type='button'
                                        onClick={handleRemoveMenuFile}
                                        className='flex justify-center items-center bg-placeholder-1 bg-opacity-80 rounded-full p-1 shadow-lg w-4.5 h-4.5'
                                    >
                                        <XIcon className='w-3.5 h-3.5 text-white' />
                                    </button>
                                </div>
                            )
                        }
                        {
                            serviceList.map((shopServiceType, index) => (
                                <ShopServiceItem
                                    key={index}
                                    title={t(`select_options.${SHOP_SERVICE[Number(shopServiceType)].key}`)}
                                    form={{
                                        control: form.control,
                                        fields,
                                        append,
                                        remove
                                    }}
                                    t={t}
                                    serviceType={Number(shopServiceType)}
                                    serviceData={fields?.length > 0 ?
                                        fields.map((service) => {
                                            if (service.type.toString() === shopServiceType) return service;
                                            return null;
                                        }) :
                                        []}
                                />
                            ))
                        }
                    </div>
                    <EditShopFormFooter code={code ?? ''} onSubmit={form.handleSubmit(onSubmit)} isSubmitDisabled={form.formState.isSubmitting} form={form} fetchData={fetchData} />
                </form>
            </Form>
        </>
    );
});

interface ShopServiceItemProps {
    title: string,
    form: Pick<UseFieldArrayReturn<FormData, 'services'>, 'fields' | 'append' | 'remove'> & Pick<UseFormReturn<FormData>, 'control'>,
    t: TFunction<'translation', undefined>,
    serviceData: Array<any>,
    serviceType: number
}

const ShopServiceItem = memo(({ title, form, t, serviceData, serviceType }: ShopServiceItemProps) => {
    // function
    const handleAddService = () => {
        form.append({
            serviceName: '',
            petWeightFrom: '',
            petWeightTo: '',
            type: Number(serviceType),
            price: '',
            isMedicalService: false
        });
    };

    return (
        <div className='mt-8'>
            <div className='border-t border-solid border-[#E8E8E8]'></div>
            <div className='text-active font-bold leading-[1.125rem] text-[0.9375rem] pt-4'>{title}</div>
            {
                serviceData && serviceData.length > 0 && serviceData.map((item, index) => (
                    item && (
                        <ShopServiceRow
                            key={item.id}
                            form={form}
                            t={t}
                            index={index}
                            serviceType={serviceType}
                        />
                    )
                ))
            }
            <div className='flex justify-center mt-4'>
                <Button type='button' onClick={handleAddService} variant='add'>
                    <img src={CircleAddIcon} alt='' className='mr-1 h-5 w-5' />
                    <span>{t('button.add_shop_service')}</span>
                </Button>
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    return JSON.stringify(prevProps.serviceData) === JSON.stringify(nextProps.serviceData) &&
        prevProps.serviceType === nextProps.serviceType &&
        prevProps.title === nextProps.title;
});

ShopServiceItem.displayName = 'ShopServiceItem';

interface ShopServiceRowProps {
    form: Pick<UseFieldArrayReturn<FormData, 'services'>, 'fields' | 'append' | 'remove'> & Pick<UseFormReturn<FormData>, 'control'>,
    t: TFunction<'translation', undefined>,
    index: number,
    serviceType: number
}

const ShopServiceRow = ({ form, t, index, serviceType }: ShopServiceRowProps) => {
    const { code } = useParams();
    const {
        shopStore: { shopInfo }
    } = useStore();
    // function
    const handleRemoveService = () => {
        form.remove(index);
    };

    return (
        <>
            <div className='relative'>
                <button
                    type='button'
                    onClick={handleRemoveService}
                    className='flex justify-center items-center absolute top-2 right-1 bg-placeholder-1 bg-opacity-80 rounded-full p-1 shadow-lg w-4.5 h-4.5'
                >
                    <XIcon className='w-3.5 h-3.5 text-white' />
                </button>
                <div className='flex md:flex-row items-start flex-col md:gap-4 gap-0 border-t border-dotted border-[#E8E8E8] md:mt-4 mt-6'>
                    {
                        (serviceType === SHOP_SERVICE_TYPE.Other || serviceType === SHOP_SERVICE_TYPE.Medical) ?
                            (
                                <>
                                    <div className='md:w-1/2 w-full'>
                                        <FormField
                                            control={form.control}
                                            name={`services.${index}.serviceName`}
                                            render={({ field }) => (
                                                <FormItem className='flex flex-col mt-4 w-full'>
                                                    <Label className='label-1' required>{t('words_title.service_name')}</Label>
                                                    <FormControl className='mt-1.5'>
                                                        <Input
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    <div className={cn('md:w-1/2 w-full', serviceType === SHOP_SERVICE_TYPE.Other && 'md:w-1/4 w-full')}>
                                        <FormField
                                            control={form.control}
                                            name={`services.${index}.price`}
                                            render={({ field }) => (
                                                <FormItem className='flex flex-col mt-4 w-full'>
                                                    <Label className='label-1'>{t('words_title.service_price')}</Label>
                                                    <FormControl>
                                                        <FormatNumber
                                                            minValue={1}
                                                            maxValue={MAX_PRICE_SERVICE}
                                                            className='mt-1.5'
                                                            decimalScale={0}
                                                            statusDisplay='input'
                                                            thousandSeparator={true}
                                                            allowNegative={false}
                                                            value={field.value}
                                                            setvalueOfNumericFormat={(values) => {
                                                                field.onChange(values === '' ? values : Number(values));
                                                            }}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    {
                                        serviceType === SHOP_SERVICE_TYPE.Other && (
                                            <div className='md:w-1/4 w-full'>
                                                <div className='flex flex-col mt-4 w-full'>
                                                    <div className='h-3.5 md:block hidden'></div>
                                                    <div className='mt-1.5 md:h-12 h-fit flex items-center'>
                                                        <FormField
                                                            control={form.control}
                                                            name={`services.${index}.isMedicalService`}
                                                            render={({ field }) => (
                                                                <FormItem className='flex items-center gap-2'>
                                                                    <FormControl>
                                                                        <Checkbox
                                                                            checked={field.value}
                                                                            onCheckedChange={field.onChange}
                                                                        />
                                                                    </FormControl>
                                                                    <Label className='label-1'>{t('words_title.medical_service_checkbox')}</Label>
                                                                    <FormMessage />
                                                                </FormItem>
                                                            )}
                                                        />

                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }

                                </>

                            ) :
                            (
                                <>
                                    <div className='md:w-1/3 w-full flex gap-4 items-start'>
                                        <FormField
                                            control={form.control}
                                            name={`services.${index}.petWeightFrom`}
                                            render={({ field }) => (
                                                <FormItem className='flex flex-col mt-4 w-full'>
                                                    <Label className='label-1' required>{t('words_title.pet_weight')}</Label>
                                                    <Select
                                                        key={field.value}
                                                        onValueChange={field.onChange}
                                                        {...field}
                                                    >
                                                        <FormControl>
                                                            <SelectTrigger className='mt-1.5' value={field.value}>
                                                                <SelectValue placeholder={t('placeholder.from')} />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {PET_WEIGHT_OPTIONS.map(item => (
                                                                <SelectItem
                                                                    className='hover:cursor-pointer'
                                                                    key={item}
                                                                    value={item.toString()}
                                                                >
                                                                    <span>{`${item} kg`}</span>
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name={`services.${index}.petWeightTo`}
                                            render={({ field }) => (
                                                <FormItem className='flex flex-col mt-4 w-full'>
                                                    <Select
                                                        key={field.value}
                                                        onValueChange={field.onChange}
                                                        {...field}
                                                    >
                                                        <FormControl>
                                                            <SelectTrigger className='mt-6.5' value={field.value}>
                                                                <SelectValue placeholder={t('placeholder.to')} />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {PET_WEIGHT_OPTIONS.map(item => (
                                                                <SelectItem
                                                                    className='hover:cursor-pointer'
                                                                    key={item}
                                                                    value={item.toString()}
                                                                >
                                                                    <span>{`${item} kg`}</span>
                                                                </SelectItem>
                                                            ))}
                                                            <SelectItem
                                                                className='hover:cursor-pointer'
                                                                value='50+'
                                                            >
                                                                <span>{`${t('words_title.up')}`}</span>
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    <div className='md:w-1/3 w-full'>
                                        <FormField
                                            control={form.control}
                                            name={`services.${index}.serviceName`}
                                            render={({ field }) => (
                                                <FormItem className='flex flex-col mt-[22px] w-full'>
                                                    <Label className='label-1' required>
                                                        {t('words_title.service_name')}
                                                    </Label>
                                                    <SearchShopServiceInput shopCode={code} valueSearch={field?.value as string} initialDisplayValue={getServiceShopNameByServiceName(field?.value as string, shopInfo?.shopServices)} setValueSearch={field.onChange}></SearchShopServiceInput>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    <div className='md:w-1/3 w-full'>
                                        <FormField
                                            control={form.control}
                                            name={`services.${index}.price`}
                                            render={({ field }) => (
                                                <FormItem className='flex flex-col mt-4 w-full'>
                                                    <Label className='label-1' required>{t('words_title.service_price')}</Label>
                                                    <FormControl>
                                                        <FormatNumber
                                                            minValue={1}
                                                            maxValue={MAX_PRICE_SERVICE}
                                                            className='mt-1.5'
                                                            decimalScale={0}
                                                            statusDisplay='input'
                                                            thousandSeparator={true}
                                                            allowNegative={false}
                                                            value={field.value}
                                                            setvalueOfNumericFormat={(values) => {
                                                                field.onChange(values === '' ? values : Number(values));
                                                            }}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </>
                            )
                    }
                </div>
                {
                    (serviceType === SHOP_SERVICE_TYPE.Other || serviceType === SHOP_SERVICE_TYPE.Medical) && (
                        <>
                            <div>
                                <div className='w-full'>
                                    <FormField
                                        control={form.control}
                                        name={`services.${index}.description`}
                                        render={({ field }) => (
                                            <FormItem className='flex flex-col mt-4 w-full'>
                                                <Label className='label-1'>{t('words_title.description_input')}</Label>
                                                <FormControl className='mt-1.5'>
                                                    <Textarea maxLength={1000} className='text-input-1 min-h-[200px]' placeholder={t('placeholder.st_about_service')} {...field} />
                                                </FormControl>
                                                <FormMessage className='text-left' />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                        </>
                    )
                }
            </div>
        </>
    );
};

interface ActionMenuDropdownProps {
    index: number,
    isDefault: any,
    handleRomoveImg: (index: number) => void,
    handleSetdefaultImg: (index: number) => void
}

const ActionMenuDropdown = ({ index, isDefault = false, handleRomoveImg, handleSetdefaultImg }: ActionMenuDropdownProps) => {
    // hooks
    const { t } = useTranslation();

    // store
    const {
        modalStore: { showAlertModal, modalWaringAlertOptions }
    } = useStore();

    const handleClickDelete = (index: number) => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            content: t('messages.confirm_delete_img'),
            onSave: () => {
                handleRomoveImg(index);
            }
        }));
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <EllipsisIcon className='h-6 w-6 text-active hover:cursor-pointer' />
            </DropdownMenuTrigger>

            <DropdownMenuContent className='w-48' align='end' forceMount>
                {
                    !isDefault && (
                        <>
                            <DropdownMenuItem className='hover:cursor-pointer' asChild>
                                <ActionDropdownItem
                                    icon={BookmarkIcon}
                                    text={t('words_title.set_default_action')}
                                    onClick={() => {
                                        handleSetdefaultImg(index);
                                    }}
                                />
                            </DropdownMenuItem>

                            <DropdownMenuSeparator />
                        </>
                    )
                }
                <DropdownMenuItem className='hover:cursor-pointer' asChild>
                    <ActionDropdownItem icon={Trash2Icon} text={t('words_title.delete_action')} onClick={() => handleClickDelete(index)} />
                </DropdownMenuItem>

            </DropdownMenuContent>
        </DropdownMenu>
    );
};
